'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Cookie } from 'lucide-react';

export type CookieConsent = 'all' | 'necessary' | null;

const COOKIE_KEY = 'winewood_cookie_consent';

export function getCookieConsent(): CookieConsent {
  if (typeof window === 'undefined') return null;
  const val = localStorage.getItem(COOKIE_KEY);
  if (val === 'all' || val === 'necessary') return val;
  return null;
}

export function setCookieConsent(value: 'all' | 'necessary'): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(COOKIE_KEY, value);
  // Dispatch event so other parts of the app can react
  window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: value }));
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const handleAcceptAll = () => {
    setCookieConsent('all');
    setVisible(false);
  };

  const handleNecessaryOnly = () => {
    setCookieConsent('necessary');
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] px-4 pb-safe"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)' }}
    >
      <div
        className="max-w-mobile mx-auto rounded-app p-4 shadow-2xl"
        style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          boxShadow: '0 -4px 32px rgba(0,0,0,0.4)',
        }}
      >
        {/* Icon + Title */}
        <div className="flex items-center gap-2 mb-2">
          <Cookie size={16} style={{ color: 'var(--primary)' }} />
          <p className="text-sm font-semibold" style={{ color: 'var(--card-foreground)' }}>
            Cookies &amp; Datenschutz
          </p>
        </div>

        {/* Description */}
        <p className="text-xs leading-relaxed mb-4" style={{ color: 'var(--muted-foreground)' }}>
          Wir verwenden technisch notwendige Cookies für den Betrieb der App. Mit „Alle akzeptieren" stimmst du zusätzlich der Nutzung optionaler Cookies (z. B. für Analysen) zu. Weitere Infos in unserer{' '}
          <Link href="/datenschutz" className="underline underline-offset-2" style={{ color: 'var(--primary)' }}>
            Datenschutzerklärung
          </Link>
          .
        </p>

        {/* Buttons */}
        <div className="flex gap-2">
          <button
            onClick={handleNecessaryOnly}
            className="flex-1 py-2.5 rounded-lg text-xs font-semibold transition-all active:scale-95"
            style={{
              background: 'var(--muted)',
              color: 'var(--muted-foreground)',
              border: '1px solid var(--border)',
            }}
          >
            Nur notwendige
          </button>
          <button
            onClick={handleAcceptAll}
            className="flex-1 py-2.5 rounded-lg text-xs font-semibold transition-all active:scale-95"
            style={{
              background: 'var(--primary)',
              color: 'var(--primary-foreground)',
            }}
          >
            Alle akzeptieren
          </button>
        </div>
      </div>
    </div>
  );
}
